import { createContext, useContext, type ReactNode } from 'react'
import { useAuth } from '@/hooks/useAuth'

type AuthValue = ReturnType<typeof useAuth>

const SessionContext = createContext<AuthValue | null>(null)

/**
 * One session for the whole panel.
 *
 * `useAuth` owns real singleton state — the in-memory access token, the 12-minute
 * rotation timer, and the once-per-address sign-in attempt. Calling it from two
 * components would run two rotation intervals against the same refresh cookie, and
 * because each rotation invalidates the previous one, the two copies would take turns
 * killing each other's token. So it is called exactly once, here, and read everywhere
 * else through context.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth()
  return <SessionContext.Provider value={auth}>{children}</SessionContext.Provider>
}

export function useSession(): AuthValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside <SessionProvider>')
  return value
}
